import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Spinner, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { useQuery, useQueryClient } from 'react-query';
import { QUATRINHLAYSOLIEU_API } from '../../../../utils/apiUrl';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';
import QuaTrinhLaySoLieuAdd from './QuaTrinhLaySoLieuAdd';
import QuaTrinhLaySoLieuEdit from './QuaTrinhLaySoLieuEdit';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const QuaTrinhLaySoLieu = ({ hoSoId, activeTab, setActiveTab, isAdmin }) => {
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit'
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const renderTooltip = (props, text) => (
        <Tooltip id="button-tooltip" {...props}>
            {text}
        </Tooltip>
    );

    const { isLoading, data: queryData, refetch } = useQuery(
        ['quaTrinhLaySoLieuData', hoSoId],
        () => Get(QUATRINHLAYSOLIEU_API.GET_PAGING, null, { HoSoNghienCuuSinhId: hoSoId, PageIndex: 1, PageSize: 100 }),
        { 
            enabled: !!hoSoId,
            keepPreviousData: true
        }
    );

    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else if (queryData.data?.$values) {
            dataList = queryData.data.$values;
        }
    }

    useEffect(() => {
        if (dataList && dataList.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = dataList.find(item => item.id === prev.id);
                    return fresh || dataList[0];
                }
                return dataList[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [dataList]);

    const handleAdd = () => {
        setViewMode('add');
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setViewMode('edit');
    };

    const handleView = (item) => {
        setDataEdit(item);
        setViewMode('view');
    };

    const confirmDelete = (item) => {
        setDeleteId(item.id);
        setConfirmDelMessage('Quá trình lấy số liệu này');
        setShowDelete(true);
    };

    const handleDelete = async () => {
        try {
            const res = await DeleteSingle(QUATRINHLAYSOLIEU_API.DELETE_ITEM, deleteId);
            if (res && (res.status === 200 || res.status === 204 || res.success)) {
                toast.success('Xóa thành công!');
                queryClient.invalidateQueries(['quaTrinhLaySoLieuData', hoSoId]);
                refetch();
                setShowDelete(false);
            } else {
                toast.error(res?.message || 'Xóa thất bại!');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra!');
        }
    };

    return (
        <>
        <Card className="h-100 card-sticky-layout">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="flex-row">
                        <Nav.Item>
                            <Nav.Link eventKey="noiLaySoLieu" className="font-weight-bold">Nơi lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="quaTrinhLaySoLieu" className="font-weight-bold">Quá trình lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="kiemTraSoLieu" className="font-weight-bold">Kiểm tra số liệu</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    {!isAdmin && (
                        <Button variant="success" size="sm" onClick={handleAdd}>
                            <i className="fas fa-plus mr-1"></i> Thêm mới
                        </Button>
                    )}
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive flex-grow-1">
                        <Table striped bordered hover>
                            <thead className="bg-light">
                                <tr>
                                    <th className="text-center" width="50">STT</th>
                                    <th className="text-center">Từ ngày</th>
                                    <th className="text-center">Đến ngày</th>
                                    <th className="text-center">Mẫu đạt tỷ lệ</th>
                                    <th className="text-center">Công bố khoa học</th>
                                    <th className="text-center">Kế hoạch số liệu tiếp theo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataList.length > 0 ? (
                                    dataList.map((item, index) => (
                                        <tr 
                                            key={item.id}
                                            onClick={() => setSelectedRow(item)}
                                            style={{ cursor: 'pointer' }}
                                            className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                        >
                                            <td className="text-center">{index + 1}</td>
                                            <td className="text-center">{item.tuNgay ? moment(item.tuNgay).format('DD/MM/YYYY') : ''}</td>
                                            <td className="text-center">{item.denNgay ? moment(item.denNgay).format('DD/MM/YYYY') : ''}</td>
                                            <td className="text-center">{item.mauDatTyLe != null ? `${item.mauDatTyLe}%` : ''}</td>
                                            <td className="text-center">{item.coCongBoKhoaHoc === true ? 'Có' : (item.coCongBoKhoaHoc === false ? 'Không' : '')}</td>
                                            <td className="text-center">{item.soLieuDat != null ? `${item.soLieuDat}%` : ''}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            Chưa có dữ liệu Quá trình lấy số liệu
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
            {!isLoading && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="info" 
                            size="sm" 
                            onClick={() => handleView(selectedRow)} 
                            className="mr-2"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-eye mr-1"></i> Xem chi tiết
                        </Button>
                        <Button 
                            variant="warning" 
                            size="sm" 
                            onClick={() => handleEdit(selectedRow)} 
                            className="mr-2 text-white"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-edit mr-1"></i> Sửa
                        </Button>
                        {!isAdmin && (
                            <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => confirmDelete(selectedRow)}
                                disabled={!selectedRow}
                            >
                                <i className="fas fa-trash mr-1"></i> Xóa
                            </Button>
                        )}
                    </div>
                </Card.Footer>
            )}
        </Card>

        {viewMode === 'add' && <QuaTrinhLaySoLieuAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />}
        {viewMode === 'edit' && dataEdit && <QuaTrinhLaySoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} isView={false} />}
        {viewMode === 'view' && dataEdit && <QuaTrinhLaySoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} isView={true} />}
        
        <ConfirmModal
            isOpen={showDelete}
            handleCancel={() => setShowDelete(false)}
            handleAccept={handleDelete}
            confirmType="delete"
            content={confirmDelMessage}
            goBackAfterDelete={false}
        />
        </>
    );
};

export default QuaTrinhLaySoLieu;
